import { nanoid } from 'nanoid';

import {
  type GridPosition,
  type InteractionSource,
  type SelectionRange,
  type SelectionState,
  type SelectionMode
} from '../../../state/types.ts';
import { type ReducerResponse, type SelectionAxis } from '../../types.ts';

type CreateNewSingleItemRangeParams = {
  state: SelectionState;
  axis: SelectionAxis;
  index: number;
  source: InteractionSource;
  mode: SelectionMode;
};

/**
 * Creates a new single-item range and adds it to selections.
 * @returns A reducer response with updated state and delta
 */
export function createNewSingleItemRange({
  state,
  axis,
  index,
  source,
  mode
}: CreateNewSingleItemRangeParams): ReducerResponse {
  const position: GridPosition =
    axis === 'row'
      ? { row: { index }, col: { index: 0 } }
      : { row: { index: 0 }, col: { index } };
  const newRange: SelectionRange = {
    id: nanoid(),
    anchor: position,
    lead: position
  };
  const updatedSelections = [...state.selections, newRange];

  const delta: ReducerResponse['delta'] = {
    kind: 'selectionModified',
    source,
    added: [newRange],
    focusChange: {
      prev: state.focusedCell,
      next: position
    }
  };

  return {
    delta,
    next: {
      ...state,
      mode,
      selections: updatedSelections,
      focusedCell: position,
      lastInteractionSource: source
    }
  };
}
